import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { ArrowUpRight, CheckCircle, Package } from 'lucide-react';

// Helper to resolve plan limits from plan metadata features list
function getPlanLimit(currentPlan, type) {
  if (!currentPlan) return '—';
  const limits = {
    enterprise: { clients: '∞', summaries: '∞', seats: '∞' },
    professional: { clients: '500', summaries: '50', seats: '5' },
    starter: { clients: '50', summaries: '5', seats: '1' },
  };
  return limits[currentPlan]?.[type] ?? '—';
}

// Derive a human-readable status badge props from subscriptionUsage
function getStatusBadge(subscriptionUsage, currentPlan) {
  if (!currentPlan) {
    return { label: 'No Plan', variant: 'secondary' };
  }
  const status = subscriptionUsage?.status;
  if (!status || status === 'active') {
    return { label: 'Active', variant: 'default' };
  }
  if (status === 'cancelled') {
    return { label: 'Cancelled', variant: 'destructive' };
  }
  if (status === 'past_due') {
    return { label: 'Past Due', variant: 'destructive' };
  }
  if (status === 'trialing') {
    return { label: 'Trial', variant: 'secondary' };
  }
  return { label: status, variant: 'secondary' };
}

export default function SubscriptionTab({
  currentPlan,
  currentPlanData,
  plans,
  subscriptionUsage,
  setShowChangePlanModal,
  setShowCancelModal,
  setSelectedNewPlan
}) {
  const { label: statusLabel, variant: statusVariant } = getStatusBadge(subscriptionUsage, currentPlan);

  // Usage values — fall back to '—' when not available from DB
  const clientsUsed = subscriptionUsage?.clients_used ?? '—';
  const aiSummaries = subscriptionUsage?.ai_summaries_used ?? '—';
  const userSeats = subscriptionUsage?.user_seats_used ?? '—';
  const billingCycle = subscriptionUsage?.billing_cycle ?? 'Monthly';
  const nextBilling = subscriptionUsage?.next_billing_date
    ? new Date(subscriptionUsage.next_billing_date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
    : '—';

  const hasNoPlan = !currentPlan || !currentPlanData;

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <Card className="border-primary/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading text-lg">Current Subscription</CardTitle>
              <CardDescription>Your active plan and subscription details</CardDescription>
            </div>
            <Badge variant={statusVariant} className="text-sm">{statusLabel}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {hasNoPlan ? (
            /* ── Empty state: no plan on record ── */
            <div className="flex flex-col items-center justify-center py-10 gap-4 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <Package className="w-8 h-8 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-foreground">No active subscription</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  You don't have a plan yet. Choose one below to get started.
                </p>
              </div>
            </div>
          ) : (
            /* ── Active plan ── */
            <>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                    <currentPlanData.icon className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold">{currentPlanData.name} Plan</h3>
                    <p className="text-sm text-muted-foreground">{currentPlanData.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-heading font-bold">
                    ${currentPlanData.price}
                    <span className="text-base font-normal text-muted-foreground">/month</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Next billing: {nextBilling}</p>
                </div>
              </div>

              <Separator className="my-6" />

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground mb-1">Clients Used</p>
                  <p className="font-heading font-bold">
                    {clientsUsed} / {getPlanLimit(currentPlan, 'clients')}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground mb-1">AI Summaries</p>
                  <p className="font-heading font-bold">
                    {aiSummaries} / {getPlanLimit(currentPlan, 'summaries')}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground mb-1">User Seats</p>
                  <p className="font-heading font-bold">
                    {userSeats} / {getPlanLimit(currentPlan, 'seats')}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground mb-1">Billing Cycle</p>
                  <p className="font-heading font-bold capitalize">{billingCycle}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                <Button onClick={() => setShowChangePlanModal(true)} data-testid="change-plan-btn">
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                  Change Plan
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCancelModal(true)}
                  className="text-destructive hover:text-destructive"
                  data-testid="cancel-subscription-btn"
                >
                  Cancel Subscription
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Plan Comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Available Plans</CardTitle>
          <CardDescription>Compare features and choose the best plan for your needs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-xl border-2 p-5 transition-all ${currentPlan === plan.id
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                  } ${plan.popular ? 'ring-2 ring-secondary ring-offset-2' : ''}`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground">
                    Most Popular
                  </Badge>
                )}
                {currentPlan === plan.id && (
                  <Badge className="absolute top-3 right-3 bg-primary">Current</Badge>
                )}

                <div className="text-center mb-4">
                  <plan.icon className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <h3 className="font-heading text-lg font-bold">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1 mt-2">
                    <span className="text-2xl font-heading font-bold">${plan.price}</span>
                    <span className="text-muted-foreground text-sm">/{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-2 mb-4">
                  {plan.features.slice(0, 5).map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-xs">
                      <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {currentPlan !== plan.id && (
                  <Button
                    className="w-full"
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={() => {
                      setSelectedNewPlan(plan.id);
                      setShowChangePlanModal(true);
                    }}
                    data-testid={`select-plan-${plan.id}`}
                  >
                    {!currentPlan
                      ? 'Select'
                      : plans.findIndex(p => p.id === plan.id) > plans.findIndex(p => p.id === currentPlan)
                        ? 'Upgrade'
                        : 'Downgrade'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
